import { ref, computed } from 'vue';
import { tools } from '@/utils/tools';

/**
 * 虚拟列表 Hook
 * @param {Object} options 配置选项
 * @param {Array|Object} options.items 数据列表（可以是数组或响应式引用）
 * @param {number} options.itemWidth 每项宽度
 * @param {number} options.bufferCount 缓冲区数量
 * @param {number} options.throttleDelay 节流延迟
 * @returns {Object} 虚拟列表相关属性和方法
 */
export default function useVirtualList(options) {
  const { items = [], itemWidth = 300, bufferCount = 4, throttleDelay = 16 } = options;

  // 滚动容器引用
  const scrollContainer = ref(null);
  // 滚动位置
  const scrollLeft = ref(0);

  // 安全获取 items 长度
  const getItemsLength = () => {
    if (Array.isArray(items)) {
      return items.length;
    }
    if (items && typeof items === 'object' && 'value' in items) {
      return items.value ? items.value.length : 0;
    }
    return 0;
  };

  // 安全获取 items 数组
  const getItemsArray = () => {
    if (Array.isArray(items)) {
      return items;
    }
    if (items && typeof items === 'object' && 'value' in items) {
      return items.value || [];
    }
    return [];
  };

  // 计算可见项
  const visibleItems = computed(() => {
    const containerWidth = scrollContainer.value?.clientWidth || 0;
    const itemsArray = getItemsArray();
    const maxIndex = itemsArray.length;

    // 计算实际的开始和结束索引
    const actualStartIndex = Math.max(0, Math.floor(scrollLeft.value / itemWidth) - bufferCount);
    const actualEndIndex = Math.min(
      maxIndex, // 确保不会超出实际数据长度
      Math.ceil((scrollLeft.value + containerWidth) / itemWidth) + bufferCount
    );

    return itemsArray.slice(actualStartIndex, actualEndIndex);
  });

  // 起始偏移计算
  const startOffset = computed(() => {
    const actualStartIndex = Math.max(0, Math.floor(scrollLeft.value / itemWidth) - bufferCount);
    return actualStartIndex * itemWidth;
  });

  // 计算总宽度
  const totalWidth = computed(() => {
    return getItemsLength() * itemWidth;
  });

  // 限制滚动范围，防止滚动超出实际数据长度
  const handleScroll = tools.throttle((event) => {
    const currentScrollLeft = event.target.scrollLeft;
    const maxScrollLeft = Math.max(0, getItemsLength() * itemWidth - (scrollContainer.value?.clientWidth || 0));

    // 限制滚动不超过最大滚动距离
    scrollLeft.value = Math.min(currentScrollLeft, maxScrollLeft);

    // 同时更新DOM元素的滚动位置，确保不会滚动超出实际数据范围
    if (currentScrollLeft > maxScrollLeft) {
      event.target.scrollLeft = maxScrollLeft;
    }
  }, throttleDelay);

  // 初始化滚动位置
  const initScrollPosition = () => {
    if (scrollContainer.value) {
      scrollLeft.value = scrollContainer.value.scrollLeft;
    }
  };

  return {
    scrollContainer,
    scrollLeft,
    visibleItems,
    startOffset,
    totalWidth,
    handleScroll,
    initScrollPosition
  };
}
